import * as React from "react";
import { Step, StepLabel, StepIconProps, Dialog } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import PatientInformation from "./NewReqPatientInformation";
import ClinicalIndication from "./NewReqClinicalIndication";
import ClinicInformation from "./NewReqClinicInformation";
import PreflightCheck from "./NewReqPreflightCheck";
import Confirmation from "./NewReqConfirmation";
import NewReqPatientSummaryBanner from "./NewReqPatientSummaryBanner";
import NewReqRequiredFieldsMessage from "./NewReqRequiredFieldsMessage";
import NewReqBillingInformation from "./NewReqBillingInformation";
import NewReqShippingInformation from "./NewReqShippingInformation";
import { Button } from "../shared/Button/Button";
import {
  EditOrderCloseButton,
  EditOrderDialogContent,
  EditOrderDialogTitle,
  NewReqDraftDelete,
  NewReqRequiredMessage,
  NewReqStepIconActive,
  NewReqStepIconCompleted,
  NewReqStepIconIdle,
  PaperWrapper,
  PatientInformationBox,
  StepperButtonMobile,
  StepperPanelDesktop,
  StepperPanelMobile,
  StepperWrapper,
  StyledFormLabel,
} from "./NewRequisition.styles";
import {
  currentEditDraftRequisition,
  currentNewRequisition,
  orderedRequisitions,
} from "../../recoil/atoms";
import { DRAFT_STATUS, NewRequisitionType, StepperIndex } from "./types";
import { TextWrap } from "../Grid/Grid.styles";
import {
  getDraftNextValidationDisabled,
  getNextValidationDisabled,
} from "./helpers";
import { WarningIcon } from "../../assets/iconComponents/Warning";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { SendActionType } from "../shared/Services/SharedServices";
import { NewReqDraftActionModal } from "./NewReqDraftActionsModal";
import { NewRequisitionFetalRiskModal } from "./NewRequisitionFetalRiskModal";
import { getAneuploidyIneligibleMessage } from "./NewReqClinicalIndicationTestPanel";
import { submitDraftRequisitionOrder } from "./Services/NewReqService";
import { convertNewReqFormToRequest } from "./Services/helpers";
import { OrderingShortcuts } from "./OrderingShortcuts";
import { RefreshNewRequisitionOrders } from "../Grid/Services/GridService";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import { GarbageCan } from "../../assets/iconComponents/GarbageCan";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { AnalyticsEventNames } from "../../utils/analyticsEvents";
import { useBooleanFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";
import { useCarrierScreenAttachmentVariant } from "./useCarrierScreenAttachmentVariant";

const getSteps = () => [
  "Clinic and Ordering Provider",
  "Patient Information",
  "Clinical Indication",
  "Billing Information",
  "Ship Kit to Patient",
  "Confirm Details",
  "",
];

const StepIcon = ({ active, completed }: StepIconProps) => {
  if (active) {
    return <NewReqStepIconActive />;
  }
  if (completed) {
    return <NewReqStepIconCompleted />;
  }
  return <NewReqStepIconIdle />;
};

const NewRequisition: React.FC<{
  handleClose: () => void;
  setTab: () => void;
  newReqOpen: boolean;
  setNewReqOpen: React.Dispatch<React.SetStateAction<boolean>>;
  draftModal: "saved" | "deleted" | "delete" | "unsaved" | undefined;
  setDraftModal: React.Dispatch<
    React.SetStateAction<"saved" | "deleted" | "delete" | "unsaved" | undefined>
  >;
  redrawDrafts?: NewRequisitionType[];
  onSelectRedrawDraft?: (order: NewRequisitionType) => void;
}> = ({
  handleClose,
  setTab,
  newReqOpen,
  setDraftModal,
  draftModal,
  setNewReqOpen,
  redrawDrafts = [],
  onSelectRedrawDraft = () => {},
}) => {
  const { auth, setLoggedOut } = useAuthenticationHook();
  const { enabled: shipKitEnabled } = useBooleanFeatureFlag(
    FeatureFlags.ShipKitToPatient,
  );
  const { variant: attachmentVariant } = useCarrierScreenAttachmentVariant();
  const isPopupAttachmentEnabled = attachmentVariant === "popup";
  const [activeStep, setActiveStep] = useState(StepperIndex.Clinic);
  const steps = getSteps();
  const stepCircleCount = shipKitEnabled ? 5 : 4;
  const newGlobalRequisition = useRecoilValue(currentNewRequisition);
  const [submittedRequisition, setSubmittedRequisition] =
    useState<NewRequisitionType>();
  const [isNextError, setIsNextError] = useState<boolean>(false);
  const [fetalRiskModalOpen, setFetalRiskModalOpen] = useState<boolean>(false);
  const [fetalRiskPromptDismissed, setFetalRiskPromptDismissed] =
    useState<boolean>(false);
  const [isDraftError, setIsDraftError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSubmitError, setIsSubmitError] = useState<boolean>(false);
  const [, setAllOrderedRequisitions] = useRecoilState(orderedRequisitions);
  const [editDraftReq, setDraftEditReq] = useRecoilState(
    currentEditDraftRequisition,
  );
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (!newReqOpen) {
      timeout = setTimeout(() => {
        setActiveStep(StepperIndex.Clinic);
        setFetalRiskModalOpen(false);
        setFetalRiskPromptDismissed(false);
      }, 500);
    }
    return () => clearTimeout(timeout);
  }, [activeStep, newReqOpen, draftModal]);

  // Open redraw drafts on the editable patient step so collection date and
  // other clinical details can be corrected before confirmation.
  // Note: Don't reset to Clinic when editDraftReq clears after submission -
  // that would interrupt the transition to the Confirmed step.
  useEffect(() => {
    if (
      newReqOpen &&
      editDraftReq?.draft_status === DRAFT_STATUS.REDRAW_DRAFT
    ) {
      setActiveStep(StepperIndex.Patient);
    }
  }, [newReqOpen, editDraftReq]);

  const handleExit = () => {
    handleClose();
    setIsDraftError(false);
    setIsNextError(false);
  };

  const totalSteps = () => steps.length;

  const setError = () => {
    const isError =
      getNextValidationDisabled(newGlobalRequisition, activeStep) ?? false;
    if (isError) {
      TrackAnalyticsEvent("NewRequisitionError", auth?.token, auth?.username, {
        step_page: steps[activeStep],
      });
    }
    setIsNextError(isError);
    return isError;
  };

  const setDraftError = () => {
    const isError =
      getDraftNextValidationDisabled(newGlobalRequisition) ?? false;
    setIsDraftError(isError);
    return isError;
  };

  function getStepContent(step: number) {
    switch (step) {
      case StepperIndex.Clinic:
        return (
          <ClinicInformation
            preflight={false}
            isError={isNextError}
            isDraftError={isDraftError}
          />
        );
      case StepperIndex.Patient:
        return (
          <PatientInformation
            preflight={false}
            isError={isNextError}
            isDraftError={isDraftError}
          />
        );
      case StepperIndex.Indication:
        return <ClinicalIndication preflight={false} isError={isNextError} />;
      case StepperIndex.Billing:
        return (
          <NewReqBillingInformation preflight={false} isError={isNextError} />
        );
      case StepperIndex.Shipping:
        return <NewReqShippingInformation preflight={false} />;
      case StepperIndex.Preflight:
        return (
          <PreflightCheck
            setActiveStep={setActiveStep}
            setSubmmittedRequisition={setSubmittedRequisition}
            setTab={setTab}
            setIsSubmitError={setIsSubmitError}
          />
        );
      case StepperIndex.Confirmed:
        return submittedRequisition ? (
          <Confirmation
            handleClose={handleExit}
            submittedRequisition={submittedRequisition}
          />
        ) : (
          <></>
        );
      default:
        return "unknown step";
    }
  }

  const advanceToNextStep = async () => {
    let nextStep = activeStep + 1;
    if (nextStep === StepperIndex.Shipping && !shipKitEnabled) {
      nextStep += 1;
    }
    const sendAction = async () => {
      const getAction = () => {
        if (nextStep === StepperIndex.Patient) {
          return "ProviderOnlineOrderPatientInfo";
        }
        if (nextStep === StepperIndex.Indication) {
          return "ProviderOnlineOrderClinicalIndication";
        }
        if (nextStep === StepperIndex.Billing) {
          return "ProviderOnlineOrderBilling";
        }
        if (nextStep === StepperIndex.Preflight) {
          return "ProviderOnlineOrderConfirm";
        }
      };
      if (activeStep === StepperIndex.Patient) {
        setDraftError();
      }
      if (getAction()) {
        await SendActionType(getAction()!, setLoggedOut, auth?.token);
      }
    };
    sendAction();

    setActiveStep(nextStep);
  };

  const isAneuploidyOnlySelected =
    newGlobalRequisition.aneuploidyNIPT &&
    !newGlobalRequisition.carrierScreen &&
    !newGlobalRequisition.isRhdOnlyOrder &&
    !newGlobalRequisition.disableAneuploidy &&
    !getAneuploidyIneligibleMessage(newGlobalRequisition);

  const handleNext = async () => {
    const topElement = document.querySelector("#top");
    topElement?.scrollIntoView();
    if (!setError()) {
      if (
        activeStep === StepperIndex.Indication &&
        !fetalRiskPromptDismissed &&
        isPopupAttachmentEnabled &&
        isAneuploidyOnlySelected
      ) {
        TrackAnalyticsEvent(
          AnalyticsEventNames.CarrierScreenAttachmentPopupShown,
          auth?.token,
          auth?.username,
          { variant: attachmentVariant },
        );
        setFetalRiskModalOpen(true);
        return;
      }
      await advanceToNextStep();
    }
  };

  const handleBack = async () => {
    const topElement = document.querySelector("#top");
    topElement?.scrollIntoView();
    let previousStep = activeStep - 1;
    if (previousStep === StepperIndex.Shipping && !shipKitEnabled) {
      previousStep -= 1;
    }
    setIsNextError(
      getNextValidationDisabled(newGlobalRequisition, previousStep) ?? false,
    );
    setIsSubmitError(false);
    setActiveStep(previousStep);
  };

  const handleSaveDraft = async () => {
    if (!auth || setDraftError()) {
      return;
    }
    setLoading(true);
    await submitDraftRequisitionOrder(
      {
        ...convertNewReqFormToRequest(newGlobalRequisition, shipKitEnabled),
        draft_status:
          editDraftReq?.draft_status === DRAFT_STATUS.REDRAW_DRAFT
            ? DRAFT_STATUS.REDRAW_DRAFT
            : DRAFT_STATUS.DRAFT,
      },
      auth.token,
      setLoggedOut,
    );
    await RefreshNewRequisitionOrders(
      auth.token,
      setAllOrderedRequisitions,
      setLoggedOut,
    );
    setLoading(false);
    setDraftModal("saved");
    setDraftEditReq(undefined);
    setNewReqOpen(false);
  };

  const showOrderingShortcuts =
    redrawDrafts.length > 0 &&
    activeStep === StepperIndex.Clinic &&
    Boolean(onSelectRedrawDraft);
  const showPatientBanner =
    activeStep >= StepperIndex.Indication &&
    activeStep < StepperIndex.Confirmed;
  const showStepTitle = activeStep !== StepperIndex.Confirmed;
  const showRequiredFieldsMessage =
    activeStep !== StepperIndex.Preflight &&
    activeStep !== StepperIndex.Confirmed &&
    activeStep !== StepperIndex.Shipping &&
    activeStep !== StepperIndex.Billing;
  const showSubmitError =
    activeStep === StepperIndex.Preflight && isSubmitError;
  const showFooter = activeStep < StepperIndex.Preflight;
  const isFirstStep = activeStep === StepperIndex.Clinic;
  const canSaveDraft = activeStep > StepperIndex.Clinic;

  if (draftModal) {
    return (
      <NewReqDraftActionModal
        type={draftModal}
        newReqForm={newGlobalRequisition}
        setDraft={setDraftModal}
        setNewReqOpen={setNewReqOpen}
      />
    );
  }

  return (
    <Dialog id="order-test-dialogue" fullWidth maxWidth="md" open={newReqOpen}>
      <NewRequisitionFetalRiskModal
        open={fetalRiskModalOpen}
        onAddCarrierScreen={() => {
          TrackAnalyticsEvent(
            AnalyticsEventNames.CarrierScreenAttachmentAccepted,
            auth?.token,
            auth?.username,
            { variant: attachmentVariant },
          );
          setFetalRiskModalOpen(false);
          setFetalRiskPromptDismissed(true);
        }}
        onClose={() => {
          TrackAnalyticsEvent(
            AnalyticsEventNames.CarrierScreenAttachmentClosed,
            auth?.token,
            auth?.username,
            { variant: attachmentVariant },
          );
          setFetalRiskModalOpen(false);
          setFetalRiskPromptDismissed(true);
        }}
        onContinueWithout={() => {
          TrackAnalyticsEvent(
            AnalyticsEventNames.CarrierScreenAttachmentDeclined,
            auth?.token,
            auth?.username,
            { variant: attachmentVariant },
          );
          setFetalRiskModalOpen(false);
          setFetalRiskPromptDismissed(true);
          advanceToNextStep();
        }}
      />
      <EditOrderDialogTitle id="customized-dialog-title">
        <TextWrap>NEW TEST</TextWrap>
        <EditOrderCloseButton aria-label="close" onClick={handleExit}>
          <CloseIcon />
        </EditOrderCloseButton>
      </EditOrderDialogTitle>
      {showOrderingShortcuts && (
        <OrderingShortcuts
          drafts={redrawDrafts}
          onSelectDraft={onSelectRedrawDraft}
        />
      )}
      <EditOrderDialogContent>
        <PatientInformationBox id="top">
          {showPatientBanner && <NewReqPatientSummaryBanner />}
          {showStepTitle && (
            <PaperWrapper square elevation={0}>
              <StyledFormLabel>{steps[activeStep]}</StyledFormLabel>
            </PaperWrapper>
          )}
          {showRequiredFieldsMessage && (
            <NewReqRequiredFieldsMessage
              isError={isNextError || isDraftError}
            />
          )}
          {showSubmitError && (
            <NewReqRequiredMessage $isError={isSubmitError}>
              {isSubmitError && <WarningIcon testId="WarningIcon" />}
              There was an error submitting the order. If the error persists,
              please contact support at (650)-460-2551.
            </NewReqRequiredMessage>
          )}
          {getStepContent(activeStep)}
        </PatientInformationBox>
      </EditOrderDialogContent>
      {showFooter && (
        <StepperPanelDesktop>
          {isFirstStep ? (
            <Button
              style={{ flex: 1 }}
              label="CANCEL"
              reverse
              size="medium"
              onClick={handleExit}
            />
          ) : (
            <Button
              style={{ flex: 1 }}
              label="BACK"
              reverse
              size="medium"
              onClick={handleBack}
            />
          )}

          <StepperWrapper activeStep={activeStep}>
            {steps.map((label, index) => {
              if (index < stepCircleCount) {
                return (
                  <Step key={label}>
                    <StepLabel StepIconComponent={StepIcon} />
                  </Step>
                );
              }
            })}
          </StepperWrapper>
          {canSaveDraft &&
            (loading ? (
              <SpinnerLoading />
            ) : (
              <>
                {editDraftReq && (
                  <NewReqDraftDelete onClick={() => setDraftModal("delete")}>
                    <GarbageCan />
                  </NewReqDraftDelete>
                )}
                <Button
                  className="save-draft-button"
                  label="SAVE DRAFT"
                  reverse
                  size="medium"
                  onClick={handleSaveDraft}
                />
              </>
            ))}

          <Button
            testId="next-button-test"
            id="new-test-next-button"
            style={{ flex: 1 }}
            disabled={activeStep === totalSteps() - 1}
            label="NEXT"
            size="medium"
            onClick={handleNext}
          />
        </StepperPanelDesktop>
      )}

      {showFooter && (
        <StepperPanelMobile>
          <StepperWrapper activeStep={activeStep}>
            {steps.map((label, index) => {
              if (index < stepCircleCount) {
                return (
                  <Step key={label}>
                    <StepLabel StepIconComponent={StepIcon} />
                  </Step>
                );
              }
            })}
          </StepperWrapper>

          <StepperButtonMobile>
            {isFirstStep ? (
              <Button
                testId="CancelButtonTest"
                style={{ flex: 1 }}
                label="CANCEL"
                reverse
                size="small"
                onClick={handleExit}
              />
            ) : (
              <Button
                testId="BackButtonTest"
                style={{ flex: 1 }}
                label="BACK"
                reverse
                size="small"
                onClick={handleBack}
              />
            )}

            <Button
              testId="NextButtonTest"
              style={{ flex: 1 }}
              disabled={activeStep === totalSteps() - 1}
              label="NEXT"
              size="small"
              onClick={handleNext}
            />
          </StepperButtonMobile>
        </StepperPanelMobile>
      )}
    </Dialog>
  );
};

export default NewRequisition;
